import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Paper,
  Autocomplete
} from '@mui/material';
import { Close, Search, MyLocation } from '@mui/icons-material';

const ViewOnMap = ({ show, onClose, currentAddress, onAddressSelect }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [searchQuery, setSearchQuery] = useState(currentAddress || '');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const geocoderRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Initialize geocoder once
  useEffect(() => {
    if (window.google && window.google.maps) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, []);

  // Initialize map and autocomplete
  useEffect(() => {
    if (show && window.google && window.google.maps) {
      const autocompleteInstance = new window.google.maps.places.AutocompleteService();
      setAutocomplete(autocompleteInstance);
      
      // Get user's current location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            const newMap = new window.google.maps.Map(mapRef.current, {
              center: userLocation,
              zoom: 15,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
            });
            
            // Initialize places service
            placesServiceRef.current = new window.google.maps.places.PlacesService(newMap);
            
            // Add click listener to map
            newMap.addListener('click', (event) => {
              placeMarker(event.latLng, newMap);
            });
            
            setMap(newMap);
            
            // If current address is provided, geocode it
            if (currentAddress) {
              geocodeAddress(currentAddress, newMap);
            } else {
              // Place marker at user's current location - FIXED: Always place marker
              const latLng = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);
              placeMarker(latLng, newMap, true); // Skip reverse geocoding on initial load
            }
          },
          (error) => {
            console.error('Error getting location:', error);
            const defaultLocation = { lat: 24.8607, lng: 67.0011 }; // Karachi
            const defaultMap = new window.google.maps.Map(mapRef.current, {
              center: defaultLocation,
              zoom: 12,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
            });
            
            placesServiceRef.current = new window.google.maps.places.PlacesService(defaultMap);
            defaultMap.addListener('click', (event) => {
              placeMarker(event.latLng, defaultMap);
            });
            
            setMap(defaultMap);
            if (currentAddress) {
              geocodeAddress(currentAddress, defaultMap);
            } else {
              // Place marker at default location if no current address
              const latLng = new window.google.maps.LatLng(defaultLocation.lat, defaultLocation.lng);
              placeMarker(latLng, defaultMap, true);
            }
          }
        );
      } else {
        // Fallback if geolocation is not supported
        const defaultLocation = { lat: 24.8607, lng: 67.0011 }; // Karachi
        const defaultMap = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        
        placesServiceRef.current = new window.google.maps.places.PlacesService(defaultMap);
        defaultMap.addListener('click', (event) => {
          placeMarker(event.latLng, defaultMap);
        });
        
        setMap(defaultMap);
        if (currentAddress) {
          geocodeAddress(currentAddress, defaultMap);
        } else {
          // Place marker at default location if no current address
          const latLng = new window.google.maps.LatLng(defaultLocation.lat, defaultLocation.lng);
          placeMarker(latLng, defaultMap, true);
        }
      }
    }
  }, [show, currentAddress]);

  // Fetch autocomplete suggestions - REDUCED DEBOUNCE to 150ms
  useEffect(() => {
    if (searchQuery.trim() && autocomplete) {
      const delayDebounceFn = setTimeout(() => {
        setIsLoading(true);
        autocomplete.getPlacePredictions(
          { input: searchQuery },
          (predictions, status) => {
            setIsLoading(false);
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions);
            } else {
              setSuggestions([]);
            }
          }
        );
      }, 150); // Reduced from 300ms to 150ms
      
      return () => clearTimeout(delayDebounceFn);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, autocomplete]);

  const placeMarker = useCallback((location, mapInstance, skipReverseGeocode = false) => {
    // Remove existing marker
    if (marker) {
      marker.setMap(null);
      setMarker(null);
    }

    // Ensure location is a LatLng object
    let latLng;
    if (location.lat && location.lng) {
      if (typeof location.lat === 'function') {
        latLng = location;
      } else {
        latLng = new window.google.maps.LatLng(location.lat, location.lng);
      }
    } else {
      console.error('Invalid location object');
      return;
    }

    // Create new marker
    const newMarker = new window.google.maps.Marker({
      position: latLng,
      map: mapInstance,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });

    // Update map center and zoom in parallel
    mapInstance.setCenter(latLng);
    mapInstance.setZoom(15);

    setMarker(newMarker);
    
    const coords = {
      lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
      lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng
    };
    
    setSelectedLocation(coords);

    // Add drag end listener
    newMarker.addListener('dragend', (event) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      setSelectedLocation({
        lat: newLat,
        lng: newLng
      });
      reverseGeocode(event.latLng);
    });

    // Only reverse geocode if needed
    if (!skipReverseGeocode) {
      reverseGeocode(latLng);
    }
  }, [marker]);

  const reverseGeocode = useCallback((location) => {
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    const latLng = typeof location.lat === 'function' 
      ? { lat: location.lat(), lng: location.lng() }
      : location;
      
    geocoderRef.current.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        setSearchQuery(results[0].formatted_address);
      } else if (status === 'OK' && results && results[1]) {
        setSearchQuery(results[1].formatted_address);
      } else {
        // Fallback to places service
        getAddressFromCoordinates(latLng.lat, latLng.lng);
      }
    });
  }, []);

  const getAddressFromCoordinates = useCallback((lat, lng) => {
    if (placesServiceRef.current) {
      const request = {
        location: new window.google.maps.LatLng(lat, lng),
        radius: 50
      };

      placesServiceRef.current.nearbySearch(request, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          setSearchQuery(results[0].name || `Location near ${results[0].vicinity}`);
        } else {
          setSearchQuery('Selected Location');
        }
      });
    } else {
      setSearchQuery('Selected Location');
    }
  }, []);

  const geocodeAddress = useCallback((address, mapInstance) => {
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    geocoderRef.current.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        placeMarker(results[0].geometry.location, mapInstance);
      } else {
        console.error('Geocode was not successful for the following reason: ' + status);
      }
    });
  }, [placeMarker]);

  const handleSearch = useCallback(() => {
    if (searchQuery.trim() && map) {
      geocodeAddress(searchQuery, map);
    }
  }, [searchQuery, map, geocodeAddress]);

  const handleSuggestionSelect = useCallback((event, value) => {
    if (value && typeof value === 'object' && value.description) {
      setSearchQuery(value.description);
      setSuggestions([]);

      if (map) {
        // Use PlacesService to get place details and location
        if (placesServiceRef.current && value.place_id) {
          const request = {
            placeId: value.place_id,
            fields: ['geometry', 'name', 'formatted_address']
          };
          
          placesServiceRef.current.getDetails(request, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
              placeMarker(place.geometry.location, map);
            } else {
              // Fallback to regular geocoding if place details fail
              geocodeAddress(value.description, map);
            }
          });
        } else {
          // Fallback to regular geocoding
          geocodeAddress(value.description, map);
        }
      }
    }
  }, [map, placeMarker, geocodeAddress]);

  const handleCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = new window.google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude
          );
          if (map) {
            placeMarker(userLocation, map);
          }
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to get your current location. Please check your browser permissions.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  }, [map, placeMarker]);

  const handleUseAddress = useCallback(() => {
    if (searchQuery && onAddressSelect) {
      onAddressSelect(searchQuery);
    }
    onClose();
  }, [searchQuery, onAddressSelect, onClose]);

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '75vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h6" component="h2">
          Select Property Location
        </Typography>
        <IconButton onClick={onClose} size="large">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Search Box with Autocomplete */}
        <Box sx={{ mb: 3,mt:2 }}>
          <Autocomplete
            freeSolo
            options={suggestions}
            getOptionLabel={(option) => 
              typeof option === 'string' ? option : option.description
            }
            loading={isLoading}
            inputValue={searchQuery}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setSearchQuery(value);
              }
            }}
            onChange={handleSuggestionSelect}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type address or search location..."
                fullWidth
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.place_id}>
                <Typography variant="body2">
                  {option.description}
                </Typography>
              </li>
            )}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<MyLocation />}
              onClick={handleCurrentLocation}
            >
              Current Location
            </Button>

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
            >
              Search Address
            </Button>
          </Box>
        </Box>

        {/* Map Container */}
        <Paper 
          ref={mapRef}
          sx={{ 
            height: '400px',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUseAddress}
            disabled={!searchQuery}
          >
            Use This Address
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewOnMap;